<script setup>
import { ref, computed, watch, nextTick, onUnmounted } from 'vue';
import { useUserStore } from '@/store/user-store';

defineProps({
  isVisible: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['close']);

const store = useUserStore();
const currentPage = ref(1); // 1 = Info page, 2 = Load recommendation page
const isGenerating = ref(false);
const isLoading = ref(false);
const recommendationData = ref(null);
const error = ref('');

// Speech API state (reused from RecommendationDialog)
const speechSynthesis = window.speechSynthesis;
const currentUtterance = ref(null);
const isPlaying = ref(false);
const isPaused = ref(false);
const currentCharIndex = ref(0);
const totalLength = ref(0);
const speechText = ref('');
const currentSentenceIndex = ref(-1);
const highlightedText = ref('');

// Computed properties (reused from RecommendationDialog)
const formattedContent = computed(() => {
  return recommendationData.value ? formatCommentary() : null;
});

const progressPercentage = computed(() => {
  if (totalLength.value === 0) return 0;
  return (currentCharIndex.value / totalLength.value) * 100;
});

// Computed property for highlighted text sections (reused from RecommendationDialog)
const highlightedSections = computed(() => {
  if (!recommendationData.value || !isPlaying.value) return null;

  const sections = ['summary', 'context', 'recommendations'];
  const result = {};

  sections.forEach(section => {
    if (recommendationData.value[section]) {
      result[section] = recommendationData.value[section].map((item, index) => {
        // Use clean text for speech matching
        const cleanText = item.replace(/\[(positive|negative|neutral)\]$/i, '').trim();
        const itemText = `${cleanText}. `;
        const itemStart = speechText.value.indexOf(itemText, getTextOffset(section, index));
        const itemEnd = itemStart + itemText.length;

        // Check if current reading position is within this item
        const isCurrentlyReading = currentCharIndex.value >= itemStart && currentCharIndex.value <= itemEnd;

        // Auto-scroll to highlighted item
        if (isCurrentlyReading) {
          nextTick(() => {
            scrollToHighlightedItem(section, index);
          });
        }

        return {
          text: item,
          isHighlighted: isCurrentlyReading
        };
      });
    }
  });

  return result;
});

// Computed property for processed sections with sentiment analysis (reused from RecommendationDialog)
const processedSections = computed(() => {
  if (!recommendationData.value) return null;

  return {
    summary: parseSentimentData(recommendationData.value.summary || []),
    context: parseSentimentData(recommendationData.value.context || []),
  };
});

// Helper function to parse sentiment and clean text (reused from RecommendationDialog)
const parseSentimentData = (items) => {
  return items.map(item => {
    const sentimentMatch = item.match(/\[(positive|negative|neutral)\]$/i);
    const sentiment = sentimentMatch ? sentimentMatch[1].toLowerCase() : 'neutral';
    const cleanText = item.replace(/\[(positive|negative|neutral)\]$/i, '').trim();
    const hasNumbers = /\d+/.test(cleanText);

    return {
      originalText: item,
      cleanText,
      sentiment,
      hasNumbers,
      icon: getIconType(sentiment, hasNumbers)
    };
  });
};

// Helper function to determine icon type (reused from RecommendationDialog)
const getIconType = (sentiment, hasNumbers) => {
  if (hasNumbers) {
    switch (sentiment) {
      case 'positive': return 'trend-up';
      case 'negative': return 'trend-down';
      case 'neutral': return 'trend-flat';
      default: return 'trend-flat';
    }
  } else {
    switch (sentiment) {
      case 'positive': return 'sentiment-positive';
      case 'negative': return 'sentiment-negative';
      case 'neutral': return 'sentiment-neutral';
      default: return 'sentiment-neutral';
    }
  }
};

const getTextOffset = (section, index) => {
  let offset = 0;

  if (section === 'summary') {
    offset = speechText.value.indexOf('Summary:') + 8; // 8 for "Summary:"
  } else if (section === 'context') {
    offset = speechText.value.indexOf('Context:') + 8; // 8 for "Context:"
  } else if (section === 'recommendations') {
    offset = speechText.value.indexOf('Recommendations:') + 16; // 16 for "Recommendations:"
  }

  // Add offset for previous items in the same section
  if (recommendationData.value[section]) {
    for (let i = 0; i < index; i++) {
      // Clean text before calculating offset
      const cleanText = recommendationData.value[section][i].replace(/\[(positive|negative|neutral)\]$/i, '').trim();
      offset += `${cleanText}. `.length;
    }
  }

  return offset;
};

const formatCommentary = () => {
  if (!recommendationData.value) return '';

  let content = 'Summary:\n';
  recommendationData.value.summary?.forEach(item => {
    // Remove sentiment tags before adding to speech text
    const cleanText = item.replace(/\[(positive|negative|neutral)\]$/i, '').trim();
    content += `${cleanText}. `;  // Add period and space for natural pause
  });

  content += '\n\nContext:\n';
  recommendationData.value.context?.forEach(item => {
    // Remove sentiment tags before adding to speech text
    const cleanText = item.replace(/\[(positive|negative|neutral)\]$/i, '').trim();
    content += `${cleanText}. `;  // Add period and space for natural pause
  });

  content += '\n\nRecommendations:\n';
  recommendationData.value.recommendations?.forEach(item => {
    // Remove sentiment tags before adding to speech text
    const cleanText = item.replace(/\[(positive|negative|neutral)\]$/i, '').trim();
    content += `${cleanText}. `;  // Add period and space for natural pause
  });

  return content;
};

const prepareSpeechText = () => {
  speechText.value = formattedContent.value || '';
  totalLength.value = speechText.value.length;
  currentCharIndex.value = 0;
};

// Audio control functions (reused from RecommendationDialog)
const createUtterance = (text, startIndex = 0) => {
  const textToSpeak = text.substring(startIndex);
  const utterance = new SpeechSynthesisUtterance(textToSpeak);

  utterance.lang = 'en-US';
  utterance.pitch = 1.0;
  utterance.rate = 1.0;
  utterance.volume = 1.0;

  const voices = speechSynthesis.getVoices();
  const englishVoice = voices.find(voice =>
    voice.lang.startsWith('en-US') || voice.lang.startsWith('en')
  );
  if (englishVoice) {
    utterance.voice = englishVoice;
  }

  utterance.onboundary = (event) => {
    if (event.name === 'word' || event.name === 'sentence') {
      currentCharIndex.value = startIndex + event.charIndex;
    }
  };

  utterance.onend = () => {
    isPlaying.value = false;
    isPaused.value = false;
    currentCharIndex.value = totalLength.value;
  };

  utterance.onerror = (event) => {
    console.error('Speech synthesis error:', event.error);
    isPlaying.value = false;
    isPaused.value = false;
  };

  return utterance;
};

const playAudio = () => {
  if (!speechText.value) return;

  if (isPaused.value) {
    speechSynthesis.resume();
    isPlaying.value = true;
    isPaused.value = false;
  } else {
    stopAudio();
    currentUtterance.value = createUtterance(speechText.value, currentCharIndex.value);
    speechSynthesis.speak(currentUtterance.value);
    isPlaying.value = true;
    isPaused.value = false;
  }
};

const pauseAudio = () => {
  if (isPlaying.value) {
    speechSynthesis.pause();
    isPlaying.value = false;
    isPaused.value = true;
  }
};

const stopAudio = () => {
  speechSynthesis.cancel();
  isPlaying.value = false;
  isPaused.value = false;
  currentUtterance.value = null;
};

const replayAudio = () => {
  stopAudio();
  currentCharIndex.value = 0;
  playAudio();
};

const backwardAudio = () => {
  if (!speechText.value) return;

  const charsPerSecond = Math.ceil(totalLength.value / 60);
  const backwardChars = charsPerSecond * 10;

  currentCharIndex.value = Math.max(0, currentCharIndex.value - backwardChars);

  if (isPlaying.value) {
    stopAudio();
    setTimeout(() => {
      playAudio();
    }, 100);
  }
};

const forwardAudio = () => {
  if (!speechText.value) return;

  const charsPerSecond = Math.ceil(totalLength.value / 60);
  const forwardChars = charsPerSecond * 10;

  currentCharIndex.value = Math.min(totalLength.value, currentCharIndex.value + forwardChars);

  if (isPlaying.value) {
    stopAudio();
    setTimeout(() => {
      playAudio();
    }, 100);
  }
};

const scrollToHighlightedItem = (section, index) => {
  const element = document.querySelector(`[data-section="${section}"][data-index="${index}"]`);
  if (element) {
    const container = element.closest('.dialog-content');
    if (container) {
      const containerRect = container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();

      const elementTop = elementRect.top - containerRect.top + container.scrollTop;
      const elementHeight = elementRect.height;
      const containerHeight = containerRect.height;

      const isVisible = elementRect.top >= containerRect.top &&
                       elementRect.bottom <= containerRect.bottom;

      if (!isVisible) {
        const scrollTo = elementTop - (containerHeight / 2) + (elementHeight / 2);
        container.scrollTo({
          top: Math.max(0, scrollTo),
          behavior: 'smooth'
        });
      }
    }
  }
};

// Helper function to get icon CSS classes (reused from RecommendationDialog)
const getIconClass = (iconType) => {
  const baseClasses = ['pi'];

  switch (iconType) {
    case 'trend-up':
      baseClasses.push('pi-arrow-up', 'trend-positive');
      break;
    case 'trend-down':
      baseClasses.push('pi-arrow-down', 'trend-negative');
      break;
    case 'trend-flat':
      baseClasses.push('pi-minus', 'trend-neutral');
      break;
    case 'sentiment-positive':
      baseClasses.push('pi-thumbs-up', 'sentiment-positive');
      break;
    case 'sentiment-negative':
      baseClasses.push('pi-thumbs-down', 'sentiment-negative');
      break;
    case 'sentiment-neutral':
      baseClasses.push('pi-stop', 'sentiment-neutral');
      break;
    default:
      baseClasses.push('pi-stop', 'sentiment-neutral');
  }

  return baseClasses.join(' ');
};

// Close dialog
const closeDialog = () => {
  stopAudio();
  currentPage.value = 1;
  isGenerating.value = false;
  isLoading.value = false;
  recommendationData.value = null;
  error.value = '';
  emit('close');
};

// Start recommendation generation
const generateRecommendation = async () => {
  isGenerating.value = true;
  error.value = '';

  try {
    await store.publishRecommendationEvent();
    // Navigate to page 2 after successful event publishing
    currentPage.value = 2;
  } catch (err) {
    error.value = 'Failed to initiate recommendation generation. Please try again.';
    console.error('Recommendation generation error:', err);
  } finally {
    isGenerating.value = false;
  }
};

// Load recommendation summary
const loadRecommendation = async () => {
  isLoading.value = true;
  error.value = '';

  try {
    const response = await store.getRecommendationSummary();

    // Check if recommendationData exists and is not null
    if (response && response.responseData && response.responseData.recommendationData) {
      const data = response.responseData.recommendationData;

      // Check if we have actual content in the recommendations
      const hasContent = (data.summary && data.summary.length > 0) ||
                        (data.context && data.context.length > 0) ||
                        (data.recommendations && data.recommendations.length > 0);

      if (hasContent) {
        recommendationData.value = data;

        // Prepare speech text
        await nextTick();
        prepareSpeechText();

        // Auto-play when content loads
        setTimeout(() => {
          playAudio();
        }, 500);
      } else {
        // No content in recommendations - being generated
        error.value = 'Recommendation is still being processed. Please try again in a few moments.';
      }
    } else {
      // recommendationData is null or missing - being generated
      error.value = 'Recommendation is still being processed. Please try again in a few moments.';
    }
  } catch (err) {
    error.value = 'Failed to load recommendation. The system might still be processing your data.';
    console.error('Load recommendation error:', err);
  } finally {
    isLoading.value = false;
  }
};

// Navigate back to page 1
const goBackToInfo = () => {
  currentPage.value = 1;
  recommendationData.value = null;
  error.value = '';
};

// Navigation back to portfolio analytics
const navigateToPortfolio = () => {
  closeDialog();
};

// Cleanup on unmount
onUnmounted(() => {
  stopAudio();
});
</script>

<template>
  <div v-if="isVisible" class="recommendation-dialog-overlay" @click.self="closeDialog">
    <div class="recommendation-dialog">
      <!-- Dialog Header -->
      <div class="dialog-header">
        <h2 class="dialog-title">
          <span v-if="currentPage === 1">Portfolio Recommendation Engine</span>
          <span v-else>Generate Your Recommendation</span>
        </h2>
        <button class="close-button" @click="closeDialog">×</button>
      </div>

      <!-- Page 1: Information Page -->
      <div v-if="currentPage === 1" class="dialog-content info-page">
        <div class="info-hero">
          <div class="hero-icon">🎯</div>
          <h3 class="hero-title">Personalized Portfolio Recommendations</h3>
          <p class="hero-subtitle">
            Our AI-powered engine analyzes your complete financial profile to deliver actionable investment insights
          </p>
        </div>

        <div class="data-sources-section">
          <h4 class="section-title">What Goes Into Your Recommendation</h4>

          <div class="data-source-grid">
            <!-- Goals -->
            <div class="data-source-card">
              <div class="source-icon goals-icon">🎯</div>
              <div class="source-content">
                <h5 class="source-title">Your Investment Goals</h5>
                <p class="source-description">Financial objectives, risk tolerance, and investment timeline</p>
              </div>
            </div>

            <!-- Financial Statements -->
            <div class="data-source-card">
              <div class="source-icon financial-icon">📊</div>
              <div class="source-content">
                <h5 class="source-title">Financial Statements</h5>
                <p class="source-description">Complete financial profile including assets, liabilities, and cash flow</p>
              </div>
            </div>

            <!-- Portfolio Analytics -->
            <div class="data-source-card">
              <div class="source-icon analytics-icon">📈</div>
              <div class="source-content">
                <h5 class="source-title">Portfolio Analytics</h5>
                <div class="source-details">
                  <ul>
                    <li>Portfolio performance snapshot</li>
                    <li>Sector allocation analysis</li>
                    <li>Benchmark sector weights comparison</li>
                    <li>Detailed holdings across asset categories</li>
                    <li>Return calculations and metrics</li>
                    <li>Current vs. target asset allocation</li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Attribution Analysis -->
            <div class="data-source-card">
              <div class="source-icon attribution-icon">🔍</div>
              <div class="source-content">
                <h5 class="source-title">Attribution Analysis</h5>
                <div class="source-details">
                  <ul>
                    <li>Stock-level investment details</li>
                    <li>Target asset allocation strategies</li>
                    <li>Full sector attribution analysis</li>
                    <li>Market-cap attribution effects</li>
                    <li>Portfolio valuation trends</li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Macro Indicators -->
            <div class="data-source-card featured">
              <div class="source-icon macro-icon">🌍</div>
              <div class="source-content">
                <h5 class="source-title">Expert Market Analysis</h5>
                <p class="source-description">
                  Macro-economic indicators and market trends carefully analyzed by our financial experts
                </p>
                <div class="expert-badge">
                  <span class="badge-text">Expert Curated</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="outcome-section">
          <div class="outcome-card">
            <h4 class="outcome-title">The Result</h4>
            <p class="outcome-description">
              Actionable portfolio recommendation summary exclusively customized to your portfolio,
              financial goals, and current market conditions.
            </p>
          </div>
        </div>

        <!-- Generate Button -->
        <div class="action-section">
          <div v-if="error" class="error-message">{{ error }}</div>
          <button
            class="generate-button"
            @click="generateRecommendation"
            :disabled="isGenerating"
          >
            <span v-if="!isGenerating" class="button-content">
              <span class="button-icon">🚀</span>
              <span class="button-text">Generate My Recommendation</span>
            </span>
            <span v-else class="button-content loading">
              <div class="loading-spinner"></div>
              <span class="button-text">Analyzing Your Portfolio...</span>
            </span>
          </button>
        </div>
      </div>

      <!-- Page 2: Load Recommendation Page -->
      <div v-if="currentPage === 2" class="dialog-content load-page">
        <div v-if="!recommendationData" class="load-section">
          <div class="processing-animation">
            <div class="processing-icon">⚡</div>
            <div class="processing-rings">
              <div class="ring ring-1"></div>
              <div class="ring ring-2"></div>
              <div class="ring ring-3"></div>
            </div>
          </div>

          <h3 class="processing-title">Recommendation Engine Activated</h3>
          <p class="processing-description">
            Our AI is analyzing your complete portfolio data and current market conditions.
            This process typically takes 2-3 minutes.
          </p>

          <div class="processing-steps">
            <div class="step-item completed">
              <div class="step-icon">✓</div>
              <span class="step-text">Data collection completed</span>
            </div>
            <div class="step-item active">
              <div class="step-icon">⚡</div>
              <span class="step-text">AI analysis in progress</span>
            </div>
            <div class="step-item">
              <div class="step-icon">📋</div>
              <span class="step-text">Generating recommendations</span>
            </div>
          </div>

          <div class="load-actions">
            <div v-if="error" class="error-message">{{ error }}</div>
            <button
              class="load-button"
              @click="loadRecommendation"
              :disabled="isLoading"
            >
              <span v-if="!isLoading" class="button-content">
                <span class="button-icon">📥</span>
                <span class="button-text">Load My Recommendation</span>
              </span>
              <span v-else class="button-content loading">
                <div class="loading-spinner"></div>
                <span class="button-text">Loading...</span>
              </span>
            </button>

            <button class="secondary-button" @click="goBackToInfo">
              ← Back to Information
            </button>
          </div>
        </div>

        <!-- Recommendation Display (if data is loaded) -->
        <div v-else class="recommendation-display">
          <div class="recommendation-header">
            <h3 class="recommendation-title">Your Personalized Recommendation</h3>
            <div class="recommendation-date">
              Generated on {{ new Date().toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }) }}
            </div>
          </div>

          <!-- Audio Controls (reused from RecommendationDialog) -->
          <div class="audio-controls">
            <button class="control-btn" @click="playAudio" :disabled="isPlaying">
              <i class="pi pi-play"></i>
              Play
            </button>
            <button class="control-btn" @click="pauseAudio" :disabled="!isPlaying">
              <i class="pi pi-pause"></i>
              Pause
            </button>
            <button class="control-btn" @click="replayAudio">
              <i class="pi pi-refresh"></i>
              Replay
            </button>
            <button class="control-btn" @click="backwardAudio">
              <i class="pi pi-step-backward"></i>
              -10s
            </button>
            <button class="control-btn" @click="forwardAudio">
              <i class="pi pi-step-forward"></i>
              +10s
            </button>
          </div>

          <!-- Progress Indicator (reused from RecommendationDialog) -->
          <div v-if="isPlaying || isPaused" class="progress-container">
            <div class="progress-bar">
              <div
                class="progress-fill"
                :style="{ width: progressPercentage + '%' }"
              ></div>
            </div>
            <span class="progress-text">{{ Math.round(progressPercentage) }}%</span>
          </div>

          <!-- Commentary Text (reused from RecommendationDialog) -->
          <div class="commentary-text">
            <div class="section">
              <h3>Summary:</h3>
              <div class="section-items">
                <div
                  v-for="(item, index) in processedSections?.summary"
                  :key="'summary-' + index"
                  class="item-row"
                  :class="{ 'highlighted': highlightedSections && highlightedSections.summary && highlightedSections.summary[index]?.isHighlighted }"
                  :data-section="'summary'"
                  :data-index="index"
                >
                  <div class="item-text">
                    • {{ item.cleanText }}
                  </div>
                  <div class="item-icon">
                    <i v-if="item.icon !== 'sentiment-neutral'" :class="getIconClass(item.icon)"></i>
                    <div v-else class="neutral-icon sentiment-neutral">
                      <svg class="neutral-arrow" viewBox="0 0 24 24" fill="none">
                        <path d="M12 2L22 12L12 22L2 12L12 2Z" fill="#FFC107" stroke="#000" stroke-width="1.5"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="section">
              <h3>Context:</h3>
              <div class="section-items">
                <div
                  v-for="(item, index) in processedSections?.context"
                  :key="'context-' + index"
                  class="item-row"
                  :class="{ 'highlighted': highlightedSections && highlightedSections.context && highlightedSections.context[index]?.isHighlighted }"
                  :data-section="'context'"
                  :data-index="index"
                >
                  <div class="item-text">
                    • {{ item.cleanText }}
                  </div>
                  <div class="item-icon">
                    <i v-if="item.icon !== 'sentiment-neutral'" :class="getIconClass(item.icon)"></i>
                    <div v-else class="neutral-icon sentiment-neutral">
                      <svg class="neutral-arrow" viewBox="0 0 24 24" fill="none">
                        <path d="M12 2L22 12L12 22L2 12L12 2Z" fill="#FFC107" stroke="#000" stroke-width="1.5"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="section">
              <h3>Recommendations:</h3>
              <div class="section-items">
                <div
                  v-for="(item, index) in processedSections?.recommendations"
                  :key="'rec-' + index"
                  class="item-row"
                  :class="{ 'highlighted': highlightedSections && highlightedSections.recommendations && highlightedSections.recommendations[index]?.isHighlighted }"
                  :data-section="'recommendations'"
                  :data-index="index"
                >
                  <div class="item-text">
                    • {{ item.cleanText }}
                  </div>
                  <div class="item-icon">
                    <i v-if="item.icon !== 'sentiment-neutral'" :class="getIconClass(item.icon)"></i>
                    <div v-else class="neutral-icon sentiment-neutral">
                      <svg class="neutral-arrow" viewBox="0 0 24 24" fill="none">
                        <path d="M12 2L22 12L12 22L2 12L12 2Z" fill="#FFC107" stroke="#000" stroke-width="1.5"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="recommendation-actions">
            <button class="primary-button" @click="navigateToPortfolio">
              Return to Portfolio Analytics
            </button>
            <button class="secondary-button" @click="goBackToInfo">
              Generate New Recommendation
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.recommendation-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
}

.recommendation-dialog {
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Dialog Header */
.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.dialog-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin: 0;
}

.close-button {
  background: none;
  border: none;
  font-size: 2rem;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  transition: color 0.2s;
}

.close-button:hover {
  color: var(--an-error);
}

/* Dialog Content */
.dialog-content {
  padding: 32px;
  overflow-y: auto;
  flex: 1;
}

/* Info Page Styles */
.info-hero {
  text-align: center;
  margin-bottom: 32px;
}

.hero-icon {
  font-size: 3rem;
  margin-bottom: 16px;
}

.hero-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin: 0 0 12px 0;
}

.hero-subtitle {
  font-size: 1.1rem;
  color: var(--an-text-muted);
  margin: 0;
  line-height: 1.5;
}

.data-sources-section {
  margin-bottom: 32px;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin-bottom: 24px;
  text-align: center;
}

.data-source-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
}

.data-source-card {
  background: #f8f9fa;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;
}

.data-source-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  border-color: var(--an-primary);
}

.data-source-card.featured {
  background: linear-gradient(135deg, #fff7ed 0%, #fef3c7 100%);
  border-color: #f59e0b;
}

.source-icon {
  font-size: 2rem;
  margin-bottom: 12px;
  text-align: center;
}

.source-title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin: 0 0 8px 0;
}

.source-description {
  font-size: 0.9rem;
  color: var(--an-text-muted);
  line-height: 1.5;
  margin: 0;
}

.source-details ul {
  margin: 8px 0 0 0;
  padding-left: 16px;
}

.source-details li {
  font-size: 0.85rem;
  color: var(--an-text-muted);
  margin-bottom: 4px;
  line-height: 1.4;
}

.expert-badge {
  margin-top: 12px;
}

.badge-text {
  background: #f59e0b;
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.outcome-section {
  margin-bottom: 32px;
}

.outcome-card {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border: 1px solid #3b82f6;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
}

.outcome-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e40af;
  margin: 0 0 12px 0;
}

.outcome-description {
  font-size: 1rem;
  color: #1e40af;
  line-height: 1.6;
  margin: 0;
}

/* Action Section */
.action-section {
  text-align: center;
}

.generate-button, .load-button, .primary-button {
  background: linear-gradient(135deg, var(--an-primary) 0%, #4A90E2 100%);
  color: white;
  border: none;
  padding: 16px 32px;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  margin: 8px;
}

.generate-button:hover, .load-button:hover, .primary-button:hover {
  background: linear-gradient(135deg, #4A90E2 0%, var(--an-primary) 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(74, 144, 226, 0.3);
}

.generate-button:disabled, .load-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.secondary-button {
  background: #f8f9fa;
  color: var(--an-text-primary);
  border: 1px solid #e5e7eb;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin: 8px;
}

.secondary-button:hover {
  background: #e5e7eb;
  border-color: #d1d5db;
}

.button-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.button-content.loading {
  gap: 12px;
}

.button-icon {
  font-size: 1.2rem;
}

.button-text {
  font-size: 1rem;
}

/* Load Page Styles */
.load-section {
  text-align: center;
}

.processing-animation {
  position: relative;
  display: inline-block;
  margin-bottom: 32px;
}

.processing-icon {
  font-size: 4rem;
  position: relative;
  z-index: 2;
}

.processing-rings {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.ring {
  position: absolute;
  border: 2px solid var(--an-primary);
  border-radius: 50%;
  opacity: 0;
  animation: pulse-ring 2s ease-out infinite;
}

.ring-1 {
  width: 80px;
  height: 80px;
  margin: -40px 0 0 -40px;
  animation-delay: 0s;
}

.ring-2 {
  width: 100px;
  height: 100px;
  margin: -50px 0 0 -50px;
  animation-delay: 0.5s;
}

.ring-3 {
  width: 120px;
  height: 120px;
  margin: -60px 0 0 -60px;
  animation-delay: 1s;
}

@keyframes pulse-ring {
  0% {
    opacity: 1;
    transform: scale(0.3);
  }
  100% {
    opacity: 0;
    transform: scale(1.2);
  }
}

.processing-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin: 0 0 16px 0;
}

.processing-description {
  font-size: 1rem;
  color: var(--an-text-muted);
  line-height: 1.5;
  margin: 0 0 32px 0;
}

.processing-steps {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 32px;
  text-align: left;
  max-width: 300px;
  margin-left: auto;
  margin-right: auto;
}

.step-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 8px;
  background: #f8f9fa;
  border: 1px solid #e5e7eb;
}

.step-item.completed {
  background: #d4edda;
  border-color: #c3e6cb;
}

.step-item.active {
  background: #fff3cd;
  border-color: #ffeaa7;
  animation: pulse-step 2s ease-in-out infinite;
}

@keyframes pulse-step {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
}

.step-icon {
  font-size: 1.2rem;
  width: 24px;
  text-align: center;
}

.step-text {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--an-text-primary);
}

.load-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

/* Recommendation Display */
.recommendation-display {
  text-align: left;
}

.recommendation-header {
  margin-bottom: 32px;
  text-align: center;
}

.recommendation-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin: 0 0 8px 0;
}

.recommendation-date {
  font-size: 0.9rem;
  color: var(--an-text-muted);
}

/* Audio Controls (reused from RecommendationDialog) */
.audio-controls {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.control-btn {
  background: var(--an-primary, #2196f3);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
}

.control-btn:hover:not(:disabled) {
  background: var(--an-primary-dark, #1976d2);
  transform: translateY(-1px);
}

.control-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
}

/* Progress Indicator (reused from RecommendationDialog) */
.progress-container {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.progress-bar {
  flex: 1;
  height: 6px;
  background: #e5e5e5;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: var(--an-primary, #2196f3);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 12px;
  color: #666;
  min-width: 35px;
}

/* Commentary Text (reused from RecommendationDialog) */
.commentary-text {
  line-height: 1.6;
  color: var(--an-text-primary, #333);
  margin-bottom: 32px;
}

.section {
  margin-bottom: 24px;
}

.section h3 {
  margin: 0 0 12px 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--an-primary, #2196f3);
  border-bottom: 2px solid var(--an-primary, #2196f3);
  padding-bottom: 4px;
}

.section-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.item-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.3s ease;
  min-height: 32px;
}

.item-text {
  flex: 1;
  font-size: 15px;
  line-height: 1.5;
  padding-left: 12px;
}

.item-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #f8f9fa;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.item-icon i {
  font-size: 16px;
  font-weight: bold;
}

.neutral-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
}

.neutral-arrow {
  width: 16px;
  height: 16px;
}

.item-row.highlighted {
  background-color: #0d7c68;
  color: white;
  font-weight: bold;
  box-shadow: 0 2px 8px rgba(13, 124, 104, 0.3);
  transform: scale(1.02);
}

/* Icon styling based on sentiment (reused from RecommendationDialog) */
.trend-positive {
  color: #28a745;
}

.trend-negative {
  color: #dc3545;
}

.trend-neutral {
  color: #6c757d;
}

.sentiment-positive {
  color: #28a745;
}

.sentiment-negative {
  color: #dc3545;
}

.sentiment-neutral {
  color: #6c757d;
}

.item-icon:has(.trend-positive),
.item-icon:has(.sentiment-positive) {
  background: #e8f5e8;
  border-color: #28a745;
}

.item-icon:has(.trend-negative),
.item-icon:has(.sentiment-negative) {
  background: #fde8e8;
  border-color: #dc3545;
}

.item-icon:has(.trend-neutral),
.item-icon:has(.sentiment-neutral) {
  background: #f1f3f4;
  border-color: #6c757d;
}

.recommendation-actions {
  display: flex;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
}

/* Responsive Design for RecommendationDialog Content */
@media (max-width: 768px) {
  .audio-controls {
    justify-content: center;
  }

  .control-btn {
    padding: 10px 14px;
    font-size: 13px;
  }

  .item-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .item-icon {
    align-self: center;
  }

  .item-text {
    padding-left: 0;
  }

  .recommendation-actions {
    flex-direction: column;
  }
}

@media (max-width: 480px) {
  .audio-controls {
    gap: 8px;
  }

  .control-btn {
    padding: 8px 12px;
    font-size: 12px;
    flex: 1;
    max-width: 80px;
  }

  .section h3 {
    font-size: 16px;
  }

  .item-text {
    font-size: 14px;
  }
}

/* Loading Spinner */
.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error Message */
.error-message {
  background: #fee;
  border: 1px solid #fbb;
  color: #c33;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 0.9rem;
}

/* Responsive Design */
@media (max-width: 768px) {
  .recommendation-dialog {
    max-width: 95%;
    max-height: 95vh;
  }

  .dialog-header, .dialog-content {
    padding: 20px;
  }

  .data-source-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .processing-steps {
    max-width: 100%;
  }

  .recommendation-actions {
    flex-direction: column;
  }
}

@media (max-width: 480px) {
  .dialog-header, .dialog-content {
    padding: 16px;
  }

  .hero-title {
    font-size: 1.5rem;
  }

  .hero-subtitle {
    font-size: 1rem;
  }
}
</style>
